/**
 * 调试相关的 Atoms
 */
import { atom, useSetAtom, getDefaultStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export interface DebugLogEntry {
  id: number;
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  data?: any;
  stack?: string;
}

/**
 * 调试面板显示开关
 */
export const debugPanelVisibleAtom = atomWithStorage(
  'debug-panel-visible',
  false // 默认隐藏
);

/**
 * 调试面板位置
 */
export const debugPanelPositionAtom = atomWithStorage(
  'debug-panel-position',
  { x: 10, y: 100 } // 避让全面屏状态栏，设置为100px
);

/**
 * 调试面板是否折叠
 */
export const debugPanelCollapsedAtom = atomWithStorage(
  'debug-panel-collapsed',
  false
);

/**
 * 调试日志存储
 */
export const debugLogsAtom = atom<DebugLogEntry[]>([]);

/**
 * 日志 ID 计数器
 */
const debugLogIdAtom = atom<number>(0);

// 最大日志条数
const MAX_LOGS = 200;

/**
 * 添加日志的 atom（内部使用）
 */
const addDebugLogAtom = atom(
  null,
  (get, set, { level, message, data, error }: {
    level: 'info' | 'warn' | 'error' | 'debug';
    message: string;
    data?: any;
    error?: Error;
  }) => {
    const currentId = get(debugLogIdAtom);
    const currentLogs = get(debugLogsAtom);

    const entry: DebugLogEntry = {
      id: currentId,
      timestamp: new Date().toLocaleTimeString('zh-CN', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3
      }),
      level,
      message,
      data,
      stack: error?.stack
    };

    // 更新 ID 计数器
    set(debugLogIdAtom, currentId + 1);

    // 保留最近的日志
    const newLogs = [...currentLogs.slice(-(MAX_LOGS - 1)), entry];
    set(debugLogsAtom, newLogs);

    // 同时在浏览器控制台输出
    const consoleArgs = [message];
    if (data !== undefined) consoleArgs.push(data);
    if (error) consoleArgs.push(error);

    switch (level) {
      case 'info':
        console.log(`[Frontend Logger]`, ...consoleArgs);
        break;
      case 'warn':
        console.warn(`[Frontend Logger]`, ...consoleArgs);
        break;
      case 'error':
        console.error(`[Frontend Logger]`, ...consoleArgs);
        break;
      case 'debug':
        console.debug(`[Frontend Logger]`, ...consoleArgs);
        break;
    }
  }
);

/**
 * 清空日志的 atom（内部使用）
 */
const clearDebugLogsAtom = atom(
  null,
  (get, set) => {
    set(debugLogsAtom, []);
    console.clear();
  }
);

/**
 * 前端日志记录器 hooks
 */
export const useFrontendLogger = () => {
  const setAddLog = useSetAtom(addDebugLogAtom);
  const setClearLogs = useSetAtom(clearDebugLogsAtom);

  return {
    info: (message: string, data?: any) => {
      setAddLog({ level: 'info', message, data });
    },
    warn: (message: string, data?: any) => {
      setAddLog({ level: 'warn', message, data });
    },
    error: (message: string, error?: Error | any, data?: any) => {
      let errorObj: Error | undefined;
      let errorData = data;

      if (error instanceof Error) {
        errorObj = error;
      } else if (error && typeof error === 'object') {
        errorData = { ...data, errorDetails: error };
      } else if (error) {
        errorObj = new Error(String(error));
      }

      setAddLog({ level: 'error', message, data: errorData, error: errorObj });
    },
    debug: (message: string, data?: any) => {
      setAddLog({ level: 'debug', message, data });
    },
    clear: () => {
      setClearLogs();
    }
  };
};

/**
 * 纯 JS 版本的前端日志记录器
 * 可以在服务层使用，不依赖 React hooks
 */
export const frontendLogger = {
  info: (message: string, data?: any) => {
    try {
      const store = getDefaultStore();
      store.set(addDebugLogAtom, { level: 'info', message, data });
    } catch (error) {
      // 如果 Jotai store 不可用，至少输出到控制台
      console.log(`[Frontend Logger] ${message}`, data);
    }
  },
  warn: (message: string, data?: any) => {
    try {
      const store = getDefaultStore();
      store.set(addDebugLogAtom, { level: 'warn', message, data });
    } catch (error) {
      console.warn(`[Frontend Logger] ${message}`, data);
    }
  },
  error: (message: string, error?: Error | any, data?: any) => {
    try {
      let errorObj: Error | undefined;
      let errorData = data;

      if (error instanceof Error) {
        errorObj = error;
      } else if (error && typeof error === 'object') {
        errorData = { ...data, errorDetails: error };
      } else if (error) {
        errorObj = new Error(String(error));
      }

      const store = getDefaultStore();
      store.set(addDebugLogAtom, { level: 'error', message, data: errorData, error: errorObj });
    } catch (storeError) {
      console.error(`[Frontend Logger] ${message}`, error, data);
    }
  },
  debug: (message: string, data?: any) => {
    try {
      const store = getDefaultStore();
      store.set(addDebugLogAtom, { level: 'debug', message, data });
    } catch (error) {
      console.debug(`[Frontend Logger] ${message}`, data);
    }
  },
  clear: () => {
    try {
      const store = getDefaultStore();
      store.set(clearDebugLogsAtom);
    } catch (error) {
      console.clear();
    }
  }
};
